import ArrSVG from '@public/icons/arr.svg';
import classNames from 'classnames';
import { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePageDataValue } from '@/shared/atoms/page-data';
import ConstructorButton from '@/shared/components/configurator/ConstructorButton';
import { useCurrentModelStateValue } from '@/shared/components/configurator/atoms/current-model-state';
import Checkbox from '@/shared/components/shared/Checkbox';
import FormInput from '@/shared/components/shared/FormInput';
import PersonalDataConsentLabel from '@/shared/components/shared/PersonalDataConsentLabel';
import PhoneInput from '@/shared/components/shared/PhoneInput';
import Select from '@/shared/components/shared/Select';
import Textarea from '@/shared/components/shared/Textarea';
import { CONFIGURATOR_WALL_COLOR_DEFAULT } from '@/shared/config/const';
import { useFormSender } from '@/shared/hooks/use-form-sender';
import { useGetCurrentUrl } from '@/shared/hooks/use-get-url';
import { useGetUtmParams } from '@/shared/hooks/use-utm/use-get-utm';
import FormResponse from './FormResponse';

type Props = React.FormHTMLAttributes<HTMLFormElement> & {
    responseRoot?: RefObject<HTMLElement>;
    fromConfigurator?: boolean;
    slug?: string;
    apiEndpoint: string;
    additionalData?: Record<string, unknown>;
};

const BookingForm = ({ responseRoot, apiEndpoint, fromConfigurator, slug, additionalData = {}, ...props }: Props) => {
    const formRef = useRef<HTMLFormElement>(null);
    const currentModelState = useCurrentModelStateValue();
    const mappedCurrentModelState = useMemo(() => {
        const obj: Record<string, string> = {};
        if (slug) {
            obj['model'] = slug;
        }
        Object.keys(currentModelState)
            .filter((key) => key !== 'openedWallColorPickerIndex')
            .forEach((key) => {
                //@ts-ignore мне лень бороться с типами, что-то кастить куда-то, код работает
                if (Array.isArray(currentModelState[key])) {
                    //@ts-ignore
                    obj[key] = currentModelState[key].map((innerItem) => innerItem.title).join(', ');
                } else {
                    if (key === 'color') {
                        if (Array.isArray(currentModelState[key].value)) {
                            if (currentModelState[key].value.length === 0) {
                                obj[key] = `Все стены цвета ${CONFIGURATOR_WALL_COLOR_DEFAULT}`;
                            } else {
                                obj[key] = currentModelState[key].value
                                    .map((color) =>
                                        typeof color === 'string' ? color : CONFIGURATOR_WALL_COLOR_DEFAULT,
                                    )
                                    .join(', ');
                            }
                        }
                    } else {
                        //@ts-ignore
                        obj[key] = currentModelState[key].title;
                    }
                }
            });
        return obj;
    }, [currentModelState, slug]);

    const pageData = usePageDataValue();

    const [appendedData, setAppendedData] = useState<Record<string, unknown>>({});

    const getUtmParams = useGetUtmParams();
    const currentUrl = useGetCurrentUrl();

    const { data, setData, isSubmitting, error, setError } = useFormSender<{
        message: string;
        messageSecondary?: string;
    }>({
        ref: formRef,
        endpointPath: apiEndpoint,
        appendedData: {
            page: (pageData?.pageId || -1).toString(),
            url: typeof window !== 'undefined' ? location.href : '',
            ...(fromConfigurator ? mappedCurrentModelState : {}),
            ...appendedData,
            ...additionalData,

            currentUrl: currentUrl.length > 0 ? currentUrl : '',
            ...Object.fromEntries(
                Object.entries(getUtmParams).filter(([_, value]) => value), // Фильтруем только непустые значения
            ),
        },
    });

    const sectionOptions = [
        {
            text: 'Ассоциации Нефтегазовый кластер. Коллективная экспозиция разработок оборудования и технологий',
            value: 'Ассоциации Нефтегазовый кластер. Коллективная экспозиция разработок оборудования и технологий',
        },
        { text: 'Крупнейшие нефтегазовые компании', value: 'Крупнейшие нефтегазовые компании' },
        {
            text: 'Нефтесервисное оборудование, услуги и материалы',
            value: 'Нефтесервисное оборудование, услуги и материалы',
        },
        {
            text: 'Обустройство месторождений и капитальное строительство',
            value: 'Обустройство месторождений и капитальное строительство',
        },
        {
            text: 'Специализированная авиация и беспилотные комплексы',
            value: 'Специализированная авиация и беспилотные комплексы',
        },
        {
            text: 'Инжиниринг и сервис технологического оборудования',
            value: 'Инжиниринг и сервис технологического оборудования',
        },
        { text: 'Цифровые решения для ТЭК', value: 'Цифровые решения для ТЭК' },
        {
            text: 'Промышленная безопасность, охрана труда и медицинское обеспечение',
            value: 'Промышленная безопасность, охрана труда и медицинское обеспечение',
        },
    ];

    useEffect(() => {
        let timeout: NodeJS.Timeout;

        if (data?.message || error) {
            timeout = setTimeout(() => {
                setError(null);
                setData(null);
            }, 5000);
        }

        return () => {
            clearTimeout(timeout);
        };
    }, [data, error, setData, setError]);

    return (
        <form {...props} ref={formRef} className={classNames('booking-form', props.className)}>
            <div className="booking-form__inputs">
                <div className="booking-form__input-group">
                    <FormInput
                        id="booking-form-first-name"
                        name="name"
                        label="Имя"
                        required
                        className="booking-form__input-field"
                        maxLength={255}
                    />
                    <FormInput
                        id="booking-form-last-name"
                        name="lastname"
                        label="Фамилия"
                        required
                        className="booking-form__input-field"
                        maxLength={255}
                    />
                </div>

                <div className="booking-form__input-group">
                    <FormInput
                        id="booking-form-company"
                        name="company"
                        label="Компания"
                        required
                        className="booking-form__input-field"
                        maxLength={255}
                    />
                    <FormInput
                        id="booking-form-city"
                        name="city"
                        label="Город"
                        required
                        className="booking-form__input-field"
                        maxLength={255}
                    />
                </div>

                <Select
                    id="booking-form-section"
                    name="section"
                    label="Тематические разделы"
                    options={sectionOptions}
                    initialValue={sectionOptions[0].value}
                    required
                    className="booking-form__input-field"
                    icon={<ArrSVG />}
                />

                <FormInput
                    id="booking-form-email"
                    type="email"
                    name="email"
                    label="Email"
                    required
                    className="booking-form__input-field"
                    maxLength={255}
                />

                <PhoneInput
                    id="booking-form-phone"
                    name="phone"
                    label="Телефон"
                    required
                    className="booking-form__input-field"
                />

                <Textarea
                    id="booking-form-message"
                    name="message"
                    label="Сообщение"
                    className="booking-form__input-field"
                    maxLength={2000}
                />
            </div>

            <div className="booking-form__checkbox-group">
                <Checkbox checkboxStyle="square" className="input-group" required name="agreementChecked" value="1">
                    <PersonalDataConsentLabel />
                </Checkbox>
                <Checkbox
                    checkboxStyle="square"
                    className="input-group"
                    required
                    name="agreementCheckedProcessingPersonalData"
                    value="1"
                >
                    Принимаю условия{' '}
                    <a className="link link--underlined" href="/privacy/" target="_blank" rel="noreferrer nofollow">
                        пользовательского соглашения
                    </a>
                </Checkbox>
                <Checkbox checkboxStyle="square" defaultChecked={true} className="input-group" name="opt_in" value="1">
                    Я согласен получать email-рассылку о новостях TNF
                </Checkbox>
                {fromConfigurator && (
                    <Checkbox
                        checkboxStyle="square"
                        defaultChecked={true}
                        className="input-group"
                        name="banners_info"
                        value="1"
                    >
                        Выслать информацию о размерах баннеров
                    </Checkbox>
                )}
            </div>

            {error?.message && (
                <div
                    className={classNames('booking-form-message', { 'is-error': !!error })}
                    dangerouslySetInnerHTML={{ __html: error.message }}
                ></div>
            )}

            <ConstructorButton
                variant="blue"
                className="booking-form__submit-button"
                disabled={isSubmitting}
                type="submit"
            >
                Отправить
            </ConstructorButton>

            {responseRoot?.current ? (
                createPortal(<FormResponse data={data} setData={setData} />, responseRoot.current)
            ) : (
                <FormResponse data={data} setData={setData} />
            )}
        </form>
    );
};

export default BookingForm;
